import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { ApiConfig } from '@/config/api.config';
import { ApiHookProps } from '@/@types/api.types';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import EToast from '@/components/everridge/EToast';
import {
	PaymentSettingsDto,
	UpdatePaymentSettingsDto,
} from '@/features/settings/types/payment-settings-dto';

type Props = {} & ApiHookProps;

export const usePaymentSettingsApi = ({}: Props = {}) => {
	const queryClient = useQueryClient();
	const axiosInstance = useAxiosAuth();

	const {
		data: paymentSettingsData,
		isLoading: isLoadingPaymentSettings,
		isError: isErrorPaymentSettings,
	} = useQuery({
		queryKey: [ApiConfig.queries.features.paymentSettings.queryKeys.details],
		queryFn: async (): Promise<PaymentSettingsDto> => {
			const url = ApiConfig.queries.features.paymentSettings.endpoints.get;
			const response = await axiosInstance.get<PaymentSettingsDto>(url);
			return response.data;
		},
	});

	const {
		mutateAsync: updatePaymentSettings,
		isPending: isUpdatingPaymentSettings,
	} = useMutation({
		mutationFn: async (dto: UpdatePaymentSettingsDto) => {
			const url = ApiConfig.queries.features.paymentSettings.endpoints.update;
			const response = await axiosInstance.put(url, dto);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: [ApiConfig.queries.features.paymentSettings.queryKeys.details],
			});
			EToast({
				variant: 'success',
				message: t('main.settings.toasts.save.success.message'),
				description: t('main.settings.toasts.save.success.description'),
			});
		},
		onError: () => {
			EToast({
				variant: 'error',
				message: t('main.settings.toasts.save.error.message'),
				description: t('main.settings.toasts.save.error.description'),
			});
		},
	});

	return {
		paymentSettingsData,
		isLoadingPaymentSettings,
		isErrorPaymentSettings,
		updatePaymentSettings,
		isUpdatingPaymentSettings,
	};
};
